import { IZendeskTicket } from "@kuika/kuika-cl-model/src/models/zendesk";
import { ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import KuikaEmptyState from "../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaPanelAccordion from "../../ui-kit/kuika-panel-accordion/kuika-panel-accordion";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import { TicketListSkeleton } from "./helpdesk-skeleton";
import "./zendesk-left-panel.scss";
import {
  getRequesterName,
  getRequesterOptions,
  groupTicketsByStatus,
  IZendeskRequesterOption,
  IZendeskTicketGroups,
  ticketMatchesCreator,
  ticketMatchesSearch
} from "./zendesk-ticket-filter";
import ZendeskTicketItem from "./zendesk-ticket-item";

interface Props {
  searchText?: string;
}

interface State {
  /**
   * The id of the ticket whose status group this panel last auto-expanded for. `null` before
   * anything has been selected. Tracking the id (not a one-shot boolean) means EVERY newly
   * selected ticket gets its group opened, not just the first one after mount — a Board→Inbox
   * remount used to reuse a stale selection and consume its one shot, so a Solved card clicked
   * on the Board never expanded the Solved group here. Groups are only ever opened, never
   * closed, so a section the user collapsed by hand stays collapsed.
   */
  expandedForTicketId: number | null;
  isOpenAccordionOpen: boolean;
  isPendingAccordionOpen: boolean;
  isSolvedAccordionOpen: boolean;
  selectedRequesterId: number | null;
}

class ZendeskLeftPanel extends PureSdComponent<Props & IWithSDProps & IWithContextProp, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      expandedForTicketId: null,
      isOpenAccordionOpen: true,
      isPendingAccordionOpen: true,
      isSolvedAccordionOpen: false,
      selectedRequesterId: null
    };
  }

  componentDidUpdate(): void {
    const selectedTicket: IZendeskTicket | null = this.props.sd.selectedTicket;
    if (!selectedTicket || this.state.expandedForTicketId === selectedTicket.id) return;

    if (selectedTicket.status === "new" || selectedTicket.status === "open") {
      this.setState({ isOpenAccordionOpen: true });
    }

    if (selectedTicket.status === "pending") {
      this.setState({ isPendingAccordionOpen: true });
    }

    if (selectedTicket.status === "closed" || selectedTicket.status === "solved") {
      this.setState({ isSolvedAccordionOpen: true });
    }

    this.setState({ expandedForTicketId: selectedTicket.id });
  }

  // A stable handler: an inline arrow here would be a new prop on every render, which defeats
  // the row's PureComponent memo and re-renders the whole list on any change.
  private onSelectTicket = (ticket: IZendeskTicket): void => {
    this.props.sd.selectTicket(ticket.id);
  };

  private renderTicket = (ticket: IZendeskTicket, requesterName?: string): ReactNode => (
    <ZendeskTicketItem
      key={ticket.id}
      ticket={ticket}
      requesterName={requesterName}
      active={this.props.sd.selectedTicket?.id === ticket.id}
      onClick={this.onSelectTicket}
    />
  );

  render() {
    // The list fetch is the slow one (measured ~4s against the live Zendesk backend) and the
    // rail used to render literally nothing for its whole duration — no rows, no empty text,
    // no spinner — beside a lone spinner on the canvas. Paired with an empty list so a REFRESH
    // of an already-populated rail keeps showing the tickets instead of dissolving into bars.
    if (this.props.sd.isTicketsLoading && this.props.sd.zendeskTicketList.length === 0) {
      return (
        <div className="zendesk-left-panel">
          <TicketListSkeleton />
        </div>
      );
    }

    const requesterNames: Record<number, string> = this.props.sd.zendeskRequesterNames;
    const requesterOptions: IZendeskRequesterOption[] = getRequesterOptions(
      this.props.sd.zendeskTicketList,
      requesterNames
    );

    // The creator filter and per-ticket creator label only make sense when tickets come from more
    // than one person (e.g. an organisation view). For a user's own tickets there is nothing to
    // distinguish, so both are hidden to avoid noise.
    const hasMultipleCreators: boolean = requesterOptions.length > 1;
    const isSelectedRequesterValid: boolean =
      this.state.selectedRequesterId !== null &&
      requesterOptions.some((option: IZendeskRequesterOption) => option.id === this.state.selectedRequesterId);
    const selectedRequesterId: number | null =
      hasMultipleCreators && isSelectedRequesterValid ? this.state.selectedRequesterId : null;

    const passesFilters = (ticket: IZendeskTicket): boolean =>
      ticketMatchesSearch(ticket, this.props.searchText) && ticketMatchesCreator(ticket, selectedRequesterId);

    const groups: IZendeskTicketGroups = groupTicketsByStatus(this.props.sd.zendeskTicketList, passesFilters);

    const hasActiveFilter: boolean = Boolean(this.props.searchText) || selectedRequesterId !== null;
    const hasAnyTicket: boolean = groups.open.length > 0 || groups.pending.length > 0 || groups.solved.length > 0;
    // Distinguishes "you have no tickets" from "your search excluded all of them" — the rail used
    // to render nothing at all for the first case, and the second only as a bare line of text.
    const hasNoTicketAtAll: boolean = this.props.sd.zendeskTicketList.length === 0;

    const creatorNameOf = (ticket: IZendeskTicket): string | undefined =>
      hasMultipleCreators ? getRequesterName(ticket, requesterNames) : undefined;

    return (
      <div className="zendesk-left-panel">
        {hasMultipleCreators && (
          <div className="zendesk-left-panel--filter">
            <KuikaSelectbox
              className="zendesk-left-panel--creator-select"
              size="sm"
              searchable
              allowClear
              value={selectedRequesterId ?? undefined}
              placeholder="Filter by creator"
              onChange={(value: string | number | null) =>
                this.setState({ selectedRequesterId: value === null || value === undefined ? null : Number(value) })
              }
              options={requesterOptions.map((option: IZendeskRequesterOption): KuikaSelectboxOption => ({
                value: option.id,
                label: option.name
              }))}
            />
          </div>
        )}
        {hasNoTicketAtAll && (
          <KuikaEmptyState svgName="message" customName="No tickets yet" description="New tickets will appear here." />
        )}
        {!hasNoTicketAtAll && !hasAnyTicket && hasActiveFilter && (
          <KuikaEmptyState
            svgName="search-off"
            customName="No Ticket found"
            description="We couldn't find any items matching your search."
          />
        )}
        {groups.open.length > 0 && (
          <KuikaPanelAccordion
            label="Open"
            count={groups.open.length}
            isOpen={this.state.isOpenAccordionOpen || hasActiveFilter}
            onChange={(value: boolean) => this.setState({ isOpenAccordionOpen: value })}
          >
            {groups.open.map((ticket: IZendeskTicket) => this.renderTicket(ticket, creatorNameOf(ticket)))}
          </KuikaPanelAccordion>
        )}
        {groups.pending.length > 0 && (
          <KuikaPanelAccordion
            label="Pending"
            count={groups.pending.length}
            isOpen={this.state.isPendingAccordionOpen || hasActiveFilter}
            onChange={(value: boolean) => this.setState({ isPendingAccordionOpen: value })}
          >
            {groups.pending.map((ticket: IZendeskTicket) => this.renderTicket(ticket, creatorNameOf(ticket)))}
          </KuikaPanelAccordion>
        )}
        {groups.solved.length > 0 && (
          <KuikaPanelAccordion
            label="Solved"
            count={groups.solved.length}
            isOpen={this.state.isSolvedAccordionOpen || hasActiveFilter}
            onChange={(value: boolean) => this.setState({ isSolvedAccordionOpen: value })}
          >
            {groups.solved.map((ticket: IZendeskTicket) => this.renderTicket(ticket, creatorNameOf(ticket)))}
          </KuikaPanelAccordion>
        )}
      </div>
    );
  }

  /** Renders on any change that reaches it — see ZendeskCanvas. */
  checkNeedRender = (): boolean => {
    return true;
  };
}
const zendeskLeftPanel = withContext<Props>(withSD<Props>(ZendeskLeftPanel));
export { zendeskLeftPanel as ZendeskLeftPanel };
